import { AlertTriangle, Gauge, MapPin, LogIn, Power, BatteryWarning, WifiOff, Wrench, Octagon, Timer, KeyRound,
  Truck, Car, CarFront, Bus, Ship, Sailboat, Tractor, Ambulance, Forklift, Plane, Train, PersonStanding, Waves,
  Pickaxe, HardHat, Factory, Container, Fuel, Construction, Drill,
  CarTaxiFront, BusFront, TramFront, TrainFront, ShipWheel, Anchor, PlaneTakeoff, Footprints } from "lucide-react";
import { CitadineIcon, PickupIcon, FourgonMoyenIcon, FourgonGrandIcon, GenerateurIcon, SoudeurIcon,
  MiniPelleIcon, PelleHydrauliqueIcon, CompacteurIcon, DumperIcon, ArticuleIcon,
  UtilitaireIcon, MotoIcon, ChargeuseIcon, EnginIcon, MaterielIcon } from "@/lib/assetIcons";

export const VEHICLE_ICONS = {
  citadine: { label: "Citadine", Comp: CitadineIcon },
  car: { label: "Voiture / Berline", Comp: Car },
  suv: { label: "SUV / 4x4", Comp: CarFront },
  pickup: { label: "Pick-up", Comp: PickupIcon },
  fourgon_moyen: { label: "Fourgon moyen", Comp: FourgonMoyenIcon },
  fourgon_grand: { label: "Grand fourgon", Comp: FourgonGrandIcon },
  van: { label: "Utilitaire / Fourgon", Comp: UtilitaireIcon },
  truck: { label: "Camion", Comp: Truck },
  bus: { label: "Bus", Comp: Bus },
  moto: { label: "Moto", Comp: MotoIcon },
  boat: { label: "Bateau", Comp: Ship },
  jetski: { label: "Jet-ski", Comp: Waves },
  sailboat: { label: "Voilier", Comp: Sailboat },
  tractor: { label: "Tracteur", Comp: Tractor },
  ambulance: { label: "Ambulance", Comp: Ambulance },
  chariot_elevateur: { label: "Chariot élévateur", Comp: Forklift },
  excavator: { label: "Pelle / Excavatrice", Comp: PelleHydrauliqueIcon },
  mini_pelle: { label: "Mini-pelle", Comp: MiniPelleIcon },
  pelle_hydraulique: { label: "Pelle hydraulique", Comp: PelleHydrauliqueIcon },
  chargeuse: { label: "Chargeuse", Comp: ChargeuseIcon },
  engin: { label: "Engin de terrassement", Comp: EnginIcon },
  compacteur: { label: "Compacteur", Comp: CompacteurIcon },
  dumper: { label: "Dumper", Comp: DumperIcon },
  articule: { label: "Tombereau articulé", Comp: ArticuleIcon },
  equipment: { label: "Matériel autonome", Comp: MaterielIcon },
  groupe_electrogene: { label: "Groupe électrogène", Comp: GenerateurIcon },
  poste_souder: { label: "Poste à souder / Atelier", Comp: SoudeurIcon },
  plane: { label: "Avion", Comp: Plane },
  train: { label: "Train", Comp: Train },
  person: { label: "Piéton", Comp: PersonStanding },
  mine: { label: "Mine / Extraction", Comp: Pickaxe },
  chantier: { label: "Engin de chantier", Comp: Construction },
  industrie: { label: "Industrie / Usine", Comp: Factory },
  benne: { label: "Benne / Conteneur", Comp: Container },
  citerne: { label: "Camion citerne", Comp: Fuel },
  foreuse: { label: "Foreuse", Comp: Drill },
  personnel: { label: "Personnel / Chantier", Comp: HardHat },
  atelier: { label: "Atelier / Maintenance", Comp: Wrench },
  taxi: { label: "Taxi", Comp: CarTaxiFront },
  minibus: { label: "Minibus / Navette", Comp: BusFront },
  tram: { label: "Tramway", Comp: TramFront },
  locomotive: { label: "Locomotive", Comp: TrainFront },
  ferry: { label: "Ferry / Navire", Comp: ShipWheel },
  port: { label: "Port / Remorqueur", Comp: Anchor },
  cargo_air: { label: "Avion cargo", Comp: PlaneTakeoff },
  livreur: { label: "Livreur / À pied", Comp: Footprints },
  forklift: { label: "Chariot élévateur", Comp: Forklift },
};

export const VEHICLE_ICON_LIST = Object.entries(VEHICLE_ICONS).map(([key, v]) => ({ key, ...v }));

// Regroupement COHÉRENT pour le sélecteur (catégories inspirées des familles usuelles de flotte).
// Les clés non listées sont rassemblées dans « Autres » -> aucune icône n'est perdue.
const _GROUPS = [
  { label: "Catégories principales", keys: ["car", "van", "pickup", "truck", "bus", "moto", "boat", "excavator", "chargeuse", "engin", "person", "equipment"] },
  { label: "Véhicules routiers", keys: ["citadine", "suv", "taxi", "minibus", "ambulance", "citerne", "benne", "fourgon_moyen", "fourgon_grand"] },
  { label: "Engins & chantier", keys: ["mini_pelle", "pelle_hydraulique", "compacteur", "dumper", "articule", "tractor", "chariot_elevateur", "forklift", "foreuse", "chantier", "personnel", "atelier", "industrie", "mine"] },
  { label: "Nautique", keys: ["sailboat", "jetski", "ferry", "port"] },
  { label: "Autres", keys: ["groupe_electrogene", "poste_souder", "plane", "cargo_air", "train", "tram", "locomotive", "livreur"] },
];
export const VEHICLE_ICON_GROUPS = (() => {
  const seen = new Set();
  const groups = _GROUPS.map((g) => {
    const items = g.keys.filter((k) => VEHICLE_ICONS[k]).map((k) => { seen.add(k); return { key: k, ...VEHICLE_ICONS[k] }; });
    return { label: g.label, items };
  }).filter((g) => g.items.length);
  const leftover = VEHICLE_ICON_LIST.filter((x) => !seen.has(x.key));
  if (leftover.length) groups.push({ label: "Divers", items: leftover });
  return groups;
})();

// Ensemble canonique (liste demandée) -> clé de registre, pour référence/fallback documenté.
export const CANONICAL_ICON_KEYS = ["car", "van", "pickup", "truck", "bus", "excavator", "chargeuse", "engin", "boat", "moto", "person", "equipment"];

export const hasVehicleIcon = (key) => !!VEHICLE_ICONS[key];
export const vehicleIconLabel = (key) => (VEHICLE_ICONS[key]?.label) || null;
export const getVehicleIcon = (key) => (VEHICLE_ICONS[key] || VEHICLE_ICONS.truck).Comp;

// Échappement HTML sûr pour injection dans une chaîne HTML (ex. L.divIcon), contexte attribut inclus.
// Traite &, <, >, " et ' — à utiliser pour toute donnée (nom véhicule, etc.) insérée dans du HTML brut.
export const escapeHtmlAttr = (s) => String(s ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

export const MARINE_ICONS = ["boat", "jetski", "sailboat"];
export const isMarine = (v) => MARINE_ICONS.includes(v?.icon);
export const KMH_TO_KN = 0.539957;
export const fmtSpeed = (kmh, marine) => (marine ? `${Math.round((kmh || 0) * KMH_TO_KN)} nds` : `${Math.round(kmh || 0)} km/h`);

export const STATUS = {
  moving: { label: "En marche", color: "#10B981" },
  stopped: { label: "À l'arrêt", color: "#EF4444" },
  idling: { label: "Ralenti", color: "#F59E0B" },
  offline: { label: "Hors ligne", color: "#71717A" },
};

// SLEEP MODE — statut MÉTIER client. « offline » (surcouche comms/veille) n'est JAMAIS présenté
// comme une panne : on retombe toujours sur le dernier mouvement connu + « depuis X ».
export const STATUS_VERB = { moving: "En mouvement depuis", idling: "Au ralenti depuis", stopped: "À l'arrêt depuis" };
export const humanDur = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), mm = m % 60;
  if (h < 24) return `${h} h${mm ? ` ${mm}` : ""}`;
  const d = Math.floor(h / 24); return `${d} j ${h % 24} h`;
};
// RÈGLE DE FRAÎCHEUR (explicite) — on distingue toujours la dernière COMMUNICATION du boîtier
// (last_gprs_at) de la dernière POSITION GPS (last_position.timestamp / last_gps_at). Une position
// GPS n'est présentée comme « en direct » (vitesse/état actuels) que si son ancienneté est sous le
// seuil de fraîcheur. Au-delà : « Dernier état connu » + ancienneté, sans vitesse présentée comme
// actuelle. Le seuil backend OFFLINE_THRESHOLD_S=300s (alertes de déconnexion) N'est PAS touché.
//
// SEUIL ADAPTÉ À L'INTERVALLE DE REMONTÉE CONFIGURÉ (en minutes) :
//  - en mouvement/ralenti : on se base sur l'intervalle EN MARCHE (trip_interval_on_min), jamais sur
//    l'intervalle à l'arrêt — une vitesse ancienne ne doit pas rester présentée comme actuelle ;
//  - à l'arrêt : 2× l'intervalle à l'arrêt (trip_interval_off_min), avec un plancher de 15 min
//    (les balises espacent leurs remontées à l'arrêt pour économiser la batterie) ;
//  - config absente/invalide : repli provisoire de 15 min (documenté).
// On NE suppose PAS que le véhicule reste arrêté : au-delà du seuil, l'état n'est plus « actuel »
// mais « dernier état connu ».
export const STALE_FALLBACK_MS = 15 * 60 * 1000;   // repli si intervalle non configuré / invalide
export const STALE_FLOOR_OFF_MS = 15 * 60 * 1000;  // plancher à l'arrêt
export const STALE_AFTER_MS = STALE_FALLBACK_MS;   // rétrocompat (repli par défaut)

const _posIntervalMin = (val) => {
  const n = Number(val);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// Seuil de fraîcheur (ms) pour l'état `key`, dérivé de l'intervalle de remontée du véhicule.
export const staleThresholdMs = (v, key) => {
  if (key === "moving" || key === "idling") {
    const onMin = _posIntervalMin(v?.trip_interval_on_min);
    if (onMin == null) return STALE_FALLBACK_MS;
    return 2 * onMin * 60 * 1000;
  }
  const offMin = _posIntervalMin(v?.trip_interval_off_min);
  if (offMin == null) return STALE_FALLBACK_MS;
  return Math.max(2 * offMin * 60 * 1000, STALE_FLOOR_OFF_MS);
};

export const businessStatus = (v) => {
  const raw = v?.status;
  const key = (raw === "moving" || raw === "idling" || raw === "stopped") ? raw : "stopped";
  const gpsTs = v?.last_position?.timestamp ? Date.parse(v.last_position.timestamp)
    : (v?.last_gps_at ? Date.parse(v.last_gps_at) : null);
  const commTs = v?.last_gprs_at ? Date.parse(v.last_gprs_at) : null;
  const sinceMs = v?.status_changed_at ? Date.parse(v.status_changed_at) : gpsTs;
  const dur = sinceMs != null ? humanDur(Date.now() - sinceMs) : "—";
  const gpsAgeMs = gpsTs != null ? Date.now() - gpsTs : null;
  const commAgeMs = commTs != null ? Date.now() - commTs : null;
  const staleMs = staleThresholdMs(v, key);
  const fresh = gpsAgeMs != null && gpsAgeMs <= staleMs;
  return {
    key, moving: key === "moving",
    color: fresh ? STATUS[key].color : STATUS.offline.color,
    label: fresh ? STATUS[key].label : "Dernier état connu",
    verb: STATUS_VERB[key], since: sinceMs, dur,
    text: `${STATUS_VERB[key]} ${dur}`,
    fresh, staleMs, gpsTs, commTs, gpsAgeMs, commAgeMs,
  };
};

export const ALERT_META = {
  exces_vitesse: { label: "Excès de vitesse", icon: Gauge, color: "#EF4444" },
  zone_entree: { label: "Entrée de zone", icon: MapPin, color: "#3B82F6" },
  poi_entree: { label: "Arrivée POI", icon: MapPin, color: "#10B981" },
  poi_sortie: { label: "Départ POI", icon: MapPin, color: "#F59E0B" },
  zone_sortie: { label: "Sortie de zone", icon: LogIn, color: "#F59E0B" },
  arret_prolonge: { label: "Arrêt prolongé", icon: Octagon, color: "#F59E0B" },
  ralenti: { label: "Marche au ralenti", icon: Timer, color: "#F59E0B" },
  contact: { label: "Contact moteur", icon: Power, color: "#71717A" },
  demarrage_non_autorise: { label: "Démarrage non autorisé", icon: KeyRound, color: "#EF4444" },
  batterie: { label: "Batterie faible", icon: BatteryWarning, color: "#EF4444" },
  reseau: { label: "Perte réseau télécom", icon: WifiOff, color: "#F59E0B" },
  deconnexion: { label: "Traceur déconnecté", icon: WifiOff, color: "#EF4444" },
  maintenance: { label: "Maintenance", icon: Wrench, color: "#3B82F6" },
  composee: { label: "Alerte composée", icon: AlertTriangle, color: "#F5821F" },
  hors_trajet: { label: "Hors trajet type", icon: AlertTriangle, color: "#EF4444" },
  dtc: { label: "Défaut moteur (DTC)", icon: AlertTriangle, color: "#EF4444" },
};

export const SEVERITY = {
  high: { label: "Critique", color: "#EF4444" },
  medium: { label: "Moyen", color: "#F59E0B" },
  low: { label: "Info", color: "#3B82F6" },
};

export const alertMeta = (t) => ALERT_META[t] || { label: t, icon: AlertTriangle, color: "#71717A" };

export const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
};

// A5 — format daté au fuseau de la société (Pacific/Noumea | Pacific/Tahiti), jamais codé en dur.
export const fmtTimeTz = (iso, tz) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { timeZone: tz || "Pacific/Noumea", day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `il y a ${s}s`;
  if (s < 3600) return `il y a ${Math.floor(s / 60)}min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)}h`;
  return `il y a ${Math.floor(s / 86400)}j`;
};
